import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import { useModifyAssetDisplayName } from '../../../../../../../api/accounts'

const useSubmitter = (form, onComplete, asset) => {
  const { handleSubmit } = form
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyDisplayName } = useModifyAssetDisplayName()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      assetId: asset.assetId,
      displayName: formData.displayName || null
    }

    try {
      setError(null)
      setProcessing(true)
      const result = await modifyDisplayName(command)
      if (result?.statusCode === 500) {
        throw new Error('Failed to set display name')
      }
      onComplete(result)
    } catch (err) {
      setError(err?.toString())
    } finally {
      setProcessing(false)
    }
  }, [modifyDisplayName, setProcessing, onComplete, asset, setError])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function ModifyDisplayNameForm ({ asset, onCancel, onComplete }) {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      assetId: asset.assetId,
      displayName: asset.displayName || ''
    }
  })

  const { submitter, processing, error } = useSubmitter(form, onComplete, asset)

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          name='displayName' control={form.control}
          rules={{ maxLength: { value: 100, message: 'Max 100 Characters' } }}
          render={(f => (
            <SydLabel label='Display Name' error={hookFormFieldErrorAdapter(f.fieldState)} description='Max 100 Characters'>
              <SydInput {...f.field} autoFocus />
            </SydLabel>
          ))}
        />
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? (<div className='__error'>{error}</div>) : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>Cancel</SydButton>
          <SydButton
            variant='primary'
            size='md'
            onClick={submitter}
            disabled={!form.formState?.isValid}
            processing={processing}
          >
            Save Display Name
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

ModifyDisplayNameForm.propTypes = {
  asset: PropTypes.shape({
    assetId: PropTypes.number,
    displayName: PropTypes.string
  }),
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default ModifyDisplayNameForm
